import React, { useState, useEffect } from "react";
import {
	Card,
	Container,
	Row,
	Col,
	Form,
	Table,
	Placeholder,
} from "react-bootstrap";
import { getOverallResults } from "../../services/resultService";
import { getSeries, getSports } from "../../services/series";
import getCountryISO2 from "country-iso-3-to-2";
import ReactCountryFlag from "react-country-flag";
import { getEvents } from "../../services/eventService";
import MobileAd from "../Ads/MobileAd";
import TopAd from "../Ads/TopAd";
import HomeTopRow from "../HomePage/components/HomeTopRow";
import LeftAd from "../Ads/LeftAd";
import RightAd from "../Ads/RightAd";
import { Helmet } from "react-helmet";
import { RootState } from "../../store/store";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { OverallRank, Result, Series } from "../../types/model";
import {
	setSelectedSeason,
	setSelectedSeries,
	setSelectedSport,
} from "../../store/slices/fantasySlice";
import RankingTable from "./components/RankingTable";
import { FantasySeries } from "../../constants/constants";

const RankingPage = () => {
	const {
		selectedSeries,
		selectedSport,
		series,
		sports,
		events,
		seasons,
		selectedSeason,
	} = useAppSelector((state: RootState) => state.fantasy);
	const dispatch = useAppDispatch();
	const [rankings, setRankings] = useState<OverallRank[]>([]);
	const [isMobile, setIsMobile] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!selectedSeries?._id) {
			const defaultSeries = series.find(
				(seriesItem) => seriesItem.name === FantasySeries.WorldCup,
			);
			if (defaultSeries) {
				dispatch(setSelectedSeries(defaultSeries));
			}
		}

		if (!selectedSport?._id) {
			const defaultSport = sports.find((sport) => sport.name === "Downhill");
			if (defaultSport) {
				dispatch(setSelectedSport(defaultSport));
			}
		}

		const defaultSeason = seasons.find((season) => season.isActive === true);
		if (!selectedSeason?._id && defaultSeason) {
			dispatch(setSelectedSeason(defaultSeason));
		}
	}, [
		dispatch,
		seasons,
		selectedSeason?._id,
		selectedSeries?._id,
		selectedSport?._id,
		series,
		sports,
	]);

	useEffect(() => {
		if (selectedSeries?._id && selectedSport?._id && selectedSeason?._id) {
			fetchOverallRankings(
				selectedSeries._id,
				selectedSport._id,
				selectedSeason._id,
			);
		}
	}, [selectedSeries, selectedSport, selectedSeason]);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		handleResize(); // Initial check
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const fetchOverallRankings = async (
		selectedSeriesId: string,
		selectedSportId: string,
		selectedSeasonId: string,
	) => {
		try {
			setLoading(true);
			const data = await getOverallResults(
				selectedSportId,
				selectedSeriesId,
				selectedSeasonId,
			);
			setRankings(data);
		} catch (error) {
			console.error("Error fetching overall rankings:", error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Container className=" w-100 text-center">
			<Helmet>
				<title>Rankings - The Race Companion</title>
				<meta
					name="description"
					content="Current Rider Rankings for UCI Enduro, Crankworx Slopestyle, UCI and Crankworx Downhill and Freeride in Crankworx, World cups, Red Bull Hardline and Red Bull Rampage."
				/>
				<meta name="viewport" content="width=device-width, initial-scale=1" />
			</Helmet>
			{/* <Row className="m-0 p-0">{isMobile ? <MobileAd /> : <TopAd />}</Row> */}
			<Container className="p-0 my-2">
				<Row className="justify-content-md-start align-items-center mt-5">
					<Col md={3}>
						<div className="text-light fs-1 fw-bold mb-3">RANKING</div>
					</Col>
					<Col md={3}>
						<Form className="mx-md-0 mx-2">
							<Form.Group className="mb-3">
								<Form.Select
									value={selectedSeason?._id}
									onChange={(e) => {
										const selected = seasons.find(
											(season) => season._id === e.target.value,
										);
										if (!selected) return;
										dispatch(setSelectedSeason(selected));
									}}
									className="bg-secondary text-info border-info"
								>
									{/* Populate with season options */}
									{Array.isArray(seasons) &&
										seasons.map((season) => (
											<option key={season._id} value={season._id}>
												{season.name}
											</option>
										))}
								</Form.Select>
							</Form.Group>
						</Form>
					</Col>
					<Col md={3}>
						<Form className="mx-md-0 mx-2">
							<Form.Group className="mb-3">
								<Form.Select
									value={selectedSeries?._id}
									onChange={(e) => {
										const selected = series.find(
											(series) => series._id === e.target.value,
										);
										if (!selected) return;
										dispatch(setSelectedSeries(selected));
									}}
									className="bg-secondary text-info border-info"
								>
									{/* Populate with series options */}
									{Array.isArray(series) &&
										series.map((series: Series) => (
											<option key={series._id} value={series._id}>
												{series.name}
											</option>
										))}
								</Form.Select>
							</Form.Group>
						</Form>
					</Col>
					<Col md={3}>
						<Form className="mx-md-0 mx-2">
							<Form.Group className="mb-3">
								<Form.Select
									value={selectedSport._id}
									onChange={(e) => {
										const selected = sports.find(
											(sport) => sport._id === e.target.value,
										);
										if (!selected) return;
										dispatch(setSelectedSport(selected));
									}}
									className="bg-secondary text-info border-info"
								>
									{/* Populate with sport options */}
									{sports.map((sport) => (
										<option key={sport._id} value={sport._id}>
											{sport.name}
										</option>
									))}
								</Form.Select>
							</Form.Group>
						</Form>
					</Col>
				</Row>
				<Row className="mt-5">
					<Col md={6}>
						<h3 className="text-primary fs-4 fw-bold mb-3">MENS RANKINGS</h3>
						{loading ? (
							<Placeholder as="div" animation="glow" className="">
								<Placeholder
									xs={11}
									className="rounded-3"
									bg="light"
									style={{ height: "23rem", width: "100%" }}
								/>
							</Placeholder>
						) : (
							<RankingTable gender="m" rankings={rankings} />
						)}
					</Col>
					<Col md={6}>
						<h3 className="text-primary fs-4 fw-bold mb-3">WOMENS RANKINGS</h3>
						{loading ? (
							<Placeholder as="div" animation="glow" className="">
								<Placeholder
									xs={11}
									className="rounded-3"
									bg="light"
									style={{ height: "23rem", width: "100%" }}
								/>
							</Placeholder>
						) : (
							<RankingTable gender="f" rankings={rankings} />
						)}
					</Col>
				</Row>
			</Container>
		</Container>
	);
};

export default RankingPage;
